// src/components/reports/Reports.js
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { reportsService } from '../../services/reportsService';
import { premisesService } from '../../services/premisesService';
import { organizationsService } from '../../services/organizationsService';
import { useAuth } from '../../hooks/useAuth';
import ReportCard from './ReportCard';
import ReportTable from './ReportTable';
import LoadingSpinner from '../common/LoadingSpinner';
import SearchableSelect from '../common/SearchableSelect';
import { usePageTitle } from '../../hooks/usePageTitle';

const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getStatusRank = (status) => {
    switch (status) {
        case 'on_leave':
            return 4;
        case 'late':
            return 3;
        case 'present':
            return 2;
        case 'absent':
            return 1;
        default:
            return 0;
    }
};

const Reports = () => {
    usePageTitle('Reports');
    const { user } = useAuth();
    const location = useLocation();
    const [premises, setPremises] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeReport, setActiveReport] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [staffSearch, setStaffSearch] = useState('');

    // Filter states
    const [filters, setFilters] = useState({
        date_range: 'month',
        start_date: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        end_date: formatLocalDate(new Date()),
        organization_id: user?.user_type === 'client_admin' || user?.user_type === 'app_admin' ? (user?.org_id || '') : '',
        premises_id: '',
        attendance_type: '',
        consider_public_holidays: true,
        status_filter: 'all',
        export: ''
    });

    // Quick stats
    const [quickStats, setQuickStats] = useState({
        avg_attendance: 0,
        total_staff: 0,
        active_premises: 0,
        avg_hours: 0
    });

    const reportTypes = [
        {
            id: 'attendance',
            title: 'Attendance Report',
            description: 'Comprehensive attendance tracking with detailed insights',
            icon: 'fas fa-calendar-check',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        ...(user?.user_type === 'core_admin' ? [{
            id: 'sales-agents',
            title: 'Sales Agent Report',
            description: 'Sales agent performance, assigned organizations, and collections',
            icon: 'fas fa-user-tie',
            gradient: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)'
        }] : [])
    ];

    useEffect(() => {
        loadInitialData();
    }, [user]);

    useEffect(() => {
        const pref = location.state?.activeReport;
        if (pref && pref !== activeReport) {
            setActiveReport(pref);
        }
    }, [location.state, activeReport]);

    useEffect(() => {
        if (activeReport) {
            loadReportData();
        }
    }, [activeReport, filters]);

    const loadInitialData = async () => {
        try {
            await Promise.all([
                loadPremises(filters.organization_id),
                user?.user_type === 'core_admin' && loadOrganizations(),
                loadQuickStats()
            ]);
        } catch (err) {
            console.error('Error loading initial data:', err);
        }
    };

    const loadPremises = async (organizationId = '') => {
        try {
            let response;
            if (user?.user_type === 'core_admin') {
                response = await premisesService.getAllPremises({
                    page: 1,
                    limit: 1000,
                    organization_id: organizationId || ''
                });
            } else {
                response = await premisesService.getPremises({ page: 1, limit: 1000 });
            }
            setPremises(response.premises || []);
        } catch (err) {
            console.error('Error loading premises:', err);
        }
    };

    const loadOrganizations = async () => {
        try {
            const response = await organizationsService.getOrganizationsList();
            // The API returns the organizations array directly in response
            setOrganizations(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('Error loading organizations:', err);
        }
    };

    const loadQuickStats = async () => {
        try {
            const response = await reportsService.getDashboardStats();
            setQuickStats({
                avg_attendance: response.avg_attendance || 0,
                total_staff: response.total_staff || 0,
                active_premises: response.active_premises || 0,
                avg_hours: response.avg_hours || 0
            });
        } catch (err) {
            console.error('Error loading quick stats:', err);
        }
    };

    const loadReportData = async () => {
        setLoading(true);
        setError(null);

        try {
            let response;
            const reportFilters = {
                date_from: filters.start_date,
                date_to: filters.end_date,
                premises_id: filters.premises_id,
                attendance_type: filters.attendance_type,
                consider_public_holidays: filters.consider_public_holidays ? 1 : 0,
                ...(user?.user_type === 'core_admin' && filters.organization_id && { organization_id: filters.organization_id })
            };

            switch (activeReport) {
                case 'attendance':
                    response = await reportsService.getAttendanceReport(reportFilters);
                    break;
                case 'sales-agents':
                    response = await reportsService.getSalesAgentsReport(reportFilters);
                    break;
                default:
                    response = { report_data: [] };
            }

            const normalizedData = Array.isArray(response?.report_data)
                ? response.report_data
                : (Array.isArray(response) ? response : []);
            setReportData(normalizedData);
        } catch (err) {
            setError(err.message);
            console.error('Error loading report data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const next = {
                ...prev,
                [key]: value
            };

            if (key === 'organization_id') {
                next.premises_id = '';
            }

            return next;
        });
    };

    useEffect(() => {
        loadPremises(filters.organization_id);
    }, [filters.organization_id, user?.user_type]);

    const handleDateRangeChange = (range) => {
        const today = new Date();
        let startDate, endDate = today;

        switch (range) {
            case 'today':
                startDate = today;
                break;
            case 'yesterday':
                startDate = new Date(today.getTime() - 24 * 60 * 60 * 1000);
                endDate = startDate;
                break;
            case 'week':
                startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case 'month':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                break;
            case 'quarter':
                const quarter = Math.floor(today.getMonth() / 3);
                startDate = new Date(today.getFullYear(), quarter * 3, 1);
                break;
            case 'year':
                startDate = new Date(today.getFullYear(), 0, 1);
                break;
            default:
                return;
        }

        setFilters(prev => ({
            ...prev,
            date_range: range,
            start_date: formatLocalDate(startDate),
            end_date: formatLocalDate(endDate)
        }));
    };

    const handleViewReport = (reportId) => {
        setActiveReport(reportId);
    };

    const handleExportReport = async (reportId, format) => {
        try {
            await reportsService.exportReport(reportId, format, filters);
            alert('Report exported as ' + format.toUpperCase() + ' successfully!');
        } catch (err) {
            alert('Error exporting report: ' + err.message);
        }
    };

    const clearFilters = () => {
        setFilters({
            date_range: 'month',
            start_date: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
            end_date: formatLocalDate(new Date()),
            organization_id: user?.user_type === 'client_admin' || user?.user_type === 'app_admin' ? (user?.org_id || '') : '',
            premises_id: '',
            attendance_type: '',
            consider_public_holidays: true,
            status_filter: 'all',
            export: ''
        });
        setStaffSearch('');
    };

    const getAttendanceStatus = (record) => {
        const status = (record?.status || '').toLowerCase();
        const actualStatus = (record?.actual_status || '').toLowerCase();

        if (status === 'holiday' || actualStatus === 'holiday') return 'holiday';
        if (status === 'on_leave') return 'on_leave';
        if (actualStatus === 'late' || status === 'late') return 'late';
        if (status === 'absent' || (!record?.sign_in_time && !record?.sign_out_time)) return 'absent';
        return 'present';
    };

    const normalizedAttendanceData = useMemo(() => {
        if (activeReport !== 'attendance') return reportData;

        const byStaffDate = new Map();

        (reportData || []).forEach((record) => {
            const staffKey = record?.staff_record_id || record?.user_id;
            const dateKey = record?.date || record?.attendance_date;
            if (!staffKey || !dateKey) return;

            const key = `${staffKey}|${dateKey}`;
            const currentStatus = getAttendanceStatus(record);

            if (!byStaffDate.has(key)) {
                byStaffDate.set(key, { ...record, derived_status: currentStatus });
                return;
            }

            const existing = byStaffDate.get(key);
            const existingStatus = existing?.derived_status || getAttendanceStatus(existing);

            // Keep better time coverage while maintaining one row per staff/day.
            const merged = { ...existing };
            if (!merged.sign_in_time || (record.sign_in_time && record.sign_in_time < merged.sign_in_time)) {
                merged.sign_in_time = record.sign_in_time;
                merged.time_in = record.time_in || record.sign_in_time;
            }
            if (!merged.sign_out_time || (record.sign_out_time && record.sign_out_time > merged.sign_out_time)) {
                merged.sign_out_time = record.sign_out_time;
                merged.time_out = record.time_out || record.sign_out_time;
            }

            // Preserve the most severe/meaningful status for analytics.
            const chosenStatus = getStatusRank(currentStatus) >= getStatusRank(existingStatus)
                ? currentStatus
                : existingStatus;
            merged.derived_status = chosenStatus;
            merged.status = chosenStatus;
            if (chosenStatus === 'late') {
                merged.actual_status = 'late';
            }

            byStaffDate.set(key, merged);
        });

        return Array.from(byStaffDate.values());
    }, [activeReport, reportData]);

    const attendanceStatusCounts = useMemo(() => {
        if (activeReport !== 'attendance') {
            return { all: 0, present: 0, absent: 0, late: 0, on_leave: 0 };
        }

        const counts = { all: normalizedAttendanceData.length, present: 0, absent: 0, late: 0, on_leave: 0 };
        normalizedAttendanceData.forEach((record) => {
            const derived = record?.derived_status || getAttendanceStatus(record);
            if (counts[derived] !== undefined) {
                counts[derived] += 1;
            }
        });
        return counts;
    }, [activeReport, normalizedAttendanceData]);

    const displayedReportData = useMemo(() => {
        let filteredData = activeReport === 'attendance' ? normalizedAttendanceData : reportData;

        if (activeReport === 'attendance' && filters.status_filter !== 'all') {
            filteredData = filteredData.filter((record) => {
                const derived = record?.derived_status || getAttendanceStatus(record);
                return derived === filters.status_filter;
            });
        }

        if (activeReport === 'attendance') {
            const q = staffSearch.trim().toLowerCase();
            if (q) {
                filteredData = filteredData.filter((record) => {
                    const searchable = [
                        record?.staff_name,
                        record?.username,
                        record?.staff_id,
                        record?.phone_number,
                        record?.designation
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();
                    return searchable.includes(q);
                });
            }
        }

        return filteredData;
    }, [activeReport, filters.status_filter, normalizedAttendanceData, reportData, staffSearch]);

    const statusCards = [
        { key: 'all', label: 'All', icon: 'fas fa-table', className: 'status-all' },
        { key: 'absent', label: 'Absence', icon: 'fas fa-user-xmark', className: 'status-absent' },
        { key: 'late', label: 'Late', icon: 'fas fa-clock', className: 'status-late' },
        { key: 'on_leave', label: 'Leave', icon: 'fas fa-person-walking-luggage', className: 'status-leave' }
    ];

    // Transform organizations and premises for SearchableSelect
    const organizationOptions = organizations.map(org => ({
        id: org.id,
        name: org.name || org.organization_name
    }));

    const premisesOptions = premises
        .filter((premise) => {
            if (!filters.organization_id) return true;
            const premiseOrgId = premise.org_id ?? premise.organization_id ?? '';
            return String(premiseOrgId) === String(filters.organization_id);
        })
        .map(premise => ({
            id: premise.id,
            name: premise.premises_name || premise.name
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return (
        <div className="reports-management">
            <div className="container">
                {/* Header */}
                <div className="page-header">
                    <div className="header-content">
                        <div className="header-icon">
                            <i className="fas fa-chart-bar"></i>
                        </div>
                        <div className="header-text">
                            <h1>Reports & Analytics</h1>
                            <p>Generate comprehensive reports and analyze attendance data</p>
                        </div>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="stats-grid">
                    <div className="stat-card stat-attendance">
                        <div className="stat-icon">
                            <i className="fas fa-chart-line"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{quickStats.avg_attendance.toFixed(1)}%</div>
                            <div className="stat-label">Average Attendance</div>
                        </div>
                    </div>

                    <div className="stat-card stat-staff">
                        <div className="stat-icon">
                            <i className="fas fa-users"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{quickStats.total_staff}</div>
                            <div className="stat-label">Total Staff</div>
                        </div>
                    </div>

                    <div className="stat-card stat-premises">
                        <div className="stat-icon">
                            <i className="fas fa-building"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{quickStats.active_premises}</div>
                            <div className="stat-label">Active Premises</div>
                        </div>
                    </div>

                    <div className="stat-card stat-hours">
                        <div className="stat-icon">
                            <i className="fas fa-clock"></i>
                        </div>
                        <div className="stat-details">
                            <div className="stat-value">{quickStats.avg_hours.toFixed(1)}</div>
                            <div className="stat-label">Avg Hours/Day</div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-section">
                    <div className="filters-header">
                        <h3>
                            <i className="fas fa-filter"></i>
                            Report Filters
                        </h3>
                        <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
                            <i className="fas fa-redo"></i>
                            Reset
                        </button>
                    </div>

                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>
                                <i className="fas fa-calendar-alt"></i>
                                Date Range
                            </label>
                            <select
                                className="form-input"
                                value={filters.date_range}
                                onChange={(e) => handleDateRangeChange(e.target.value)}
                            >
                                <option value="today">Today</option>
                                <option value="yesterday">Yesterday</option>
                                <option value="week">This Week</option>
                                <option value="month">This Month</option>
                                <option value="quarter">This Quarter</option>
                                <option value="year">This Year</option>
                                <option value="custom">Custom Range</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>
                                <i className="fas fa-calendar-day"></i>
                                From Date
                            </label>
                            <input
                                type="date"
                                className="form-input"
                                value={filters.start_date}
                                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label>
                                <i className="fas fa-calendar-day"></i>
                                To Date
                            </label>
                            <input
                                type="date"
                                className="form-input"
                                value={filters.end_date}
                                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                            />
                        </div>

                        {user?.user_type === 'core_admin' && (
                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-building"></i>
                                    Organization
                                </label>
                                <SearchableSelect
                                    options={organizationOptions}
                                    value={filters.organization_id}
                                    onChange={(value) => handleFilterChange('organization_id', value)}
                                    placeholder="All Organizations"
                                    displayKey="name"
                                    valueKey="id"
                                    searchPlaceholder="Search organizations..."
                                    className="custom-select"
                                />
                            </div>
                        )}

                        <div className="filter-group">
                            <label>
                                <i className="fas fa-map-marker-alt"></i>
                                Premises
                            </label>
                            <SearchableSelect
                                options={premisesOptions}
                                value={filters.premises_id}
                                onChange={(value) => handleFilterChange('premises_id', value)}
                                placeholder="All Premises"
                                displayKey="name"
                                valueKey="id"
                                searchPlaceholder="Search premises..."
                                className="custom-select"
                            />
                        </div>

                        {activeReport !== 'sales-agents' && (
                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-location-dot"></i>
                                    Attendance Location
                                </label>
                                <select
                                    className="form-input"
                                    value={filters.attendance_type}
                                    onChange={(e) => handleFilterChange('attendance_type', e.target.value)}
                                >
                                    <option value="">All Locations</option>
                                    <option value="in_premises">In-Premises</option>
                                    <option value="offsite">Offsite</option>
                                </select>
                            </div>
                        )}

                        {activeReport === 'attendance' && (
                            <div className="filter-group checkbox-filter-group">
                                <label className="checkbox-inline-label">
                                    <input
                                        type="checkbox"
                                        checked={!!filters.consider_public_holidays}
                                        onChange={(e) => handleFilterChange('consider_public_holidays', e.target.checked)}
                                    />
                                    <span>Consider Public Holidays</span>
                                </label>
                            </div>
                        )}

                        {activeReport === 'attendance' && (
                            <div className="filter-group">
                                <label>
                                    <i className="fas fa-magnifying-glass"></i>
                                    Search Staff
                                </label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={staffSearch}
                                    onChange={(e) => setStaffSearch(e.target.value)}
                                    placeholder="Name, Staff ID, username, phone, designation"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Reports Grid */}
                {!activeReport ? (
                    <div className="reports-grid">
                        {reportTypes.map(report => (
                            <ReportCard
                                key={report.id}
                                report={report}
                                onView={() => handleViewReport(report.id)}
                                onExport={(format) => handleExportReport(report.id, format)}
                            />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Back Button */}
                        <div className="report-navigation">
                            <button
                                className="btn btn-back"
                                onClick={() => setActiveReport(null)}
                            >
                                <i className="fas fa-arrow-left"></i>
                                Back to Reports
                            </button>
                            <h2>{reportTypes.find(r => r.id === activeReport)?.title}</h2>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="error-message">
                                <i className="fas fa-exclamation-triangle"></i>
                                <span>{error}</span>
                            </div>
                        )}

                        {activeReport === 'attendance' && (
                            <div className="status-cards-grid">
                                {statusCards.map((card) => (
                                    <button
                                        key={card.key}
                                        type="button"
                                        className={`status-filter-card ${card.className} ${filters.status_filter === card.key ? 'active' : ''}`}
                                        onClick={() => handleFilterChange('status_filter', card.key)}
                                    >
                                        <div className="status-filter-icon">
                                            <i className={card.icon}></i>
                                        </div>
                                        <div className="status-filter-content">
                                            <span className="status-filter-label">{card.label}</span>
                                            <strong className="status-filter-value">{attendanceStatusCounts[card.key] || 0}</strong>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Report Table */}
                        {loading ? (
                            <div className="loading-container">
                                <LoadingSpinner />
                                <p>Generating report...</p>
                            </div>
                        ) : (
                            <ReportTable
                                reportType={activeReport}
                                data={displayedReportData}
                                onExport={(format) => handleExportReport(activeReport, format)}
                            />
                        )}
                    </>
                )}
            </div>

            <style jsx>{`
        .reports-management {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 2rem 0;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .page-header {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
          border-left: 5px solid #667eea;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .header-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: white;
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
        }

        .header-text h1 {
          color: #2d3748;
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 700;
        }

        .header-text p {
          color: #718096;
          margin: 0;
          font-size: 1rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: center;
          gap: 1.2rem;
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-card.stat-attendance {
          border-left-color: #667eea;
        }

        .stat-card.stat-attendance .stat-icon {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .stat-card.stat-staff {
          border-left-color: #10b981;
        }

        .stat-card.stat-staff .stat-icon {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .stat-card.stat-premises {
          border-left-color: #f59e0b;
        }

        .stat-card.stat-premises .stat-icon {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }

        .stat-card.stat-hours {
          border-left-color: #8b5cf6;
        }

        .stat-card.stat-hours .stat-icon {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .stat-details {
          flex: 1;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #2d3748;
          line-height: 1;
          margin-bottom: 0.3rem;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #718096;
          font-weight: 500;
        }

        .filters-section {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
        }

        .filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .filters-header h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #2d3748;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.7rem;
        }

        .filters-header h3 i {
          color: #667eea;
        }

        .btn-ghost {
          background: transparent;
          border: 2px solid #e2e8f0;
          color: #718096;
          padding: 0.6rem 1rem;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-ghost:hover {
          border-color: #667eea;
          color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .filters-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 1.2rem;
          align-items: flex-end;
        }

        .filter-group {
          flex: 1 1 auto;
          min-width: 200px;
          max-width: 280px;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        .filter-group label {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-group label i {
          color: #667eea;
          font-size: 0.85rem;
        }

        .checkbox-filter-group {
          align-self: end;
        }

        .checkbox-inline-label {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.55rem;
          cursor: pointer;
          color: #2d3748;
          font-weight: 600;
          line-height: 1.3;
        }

        .checkbox-inline-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          margin: 0;
          accent-color: #667eea;
          cursor: pointer;
          flex-shrink: 0;
        }

        .form-input {
          padding: 0.75rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .reports-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .report-navigation {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .report-navigation h2 {
          color: #2d3748;
          margin: 0;
          font-size: 1.8rem;
        }

        .btn-back {
          background: white;
          border: 2px solid #e2e8f0;
          color: #2d3748;
          padding: 0.75rem 1.25rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-back:hover {
          border-color: #667eea;
          color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .error-message {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          border-left: 4px solid #dc2626;
          font-weight: 500;
        }

        .loading-container {
          background: white;
          padding: 4rem 2rem;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          color: #718096;
        }

        .status-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 1.25rem;
        }

        .status-filter-card {
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 14px;
          padding: 0.9rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          width: 100%;
        }

        .status-filter-card:hover {
          border-color: #cbd5e1;
          transform: translateY(-1px);
        }

        .status-filter-card.active {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .status-filter-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1rem;
          flex-shrink: 0;
        }

        .status-filter-content {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          min-width: 0;
        }

        .status-filter-label {
          color: #475569;
          font-size: 0.88rem;
          font-weight: 600;
        }

        .status-filter-value {
          color: #0f172a;
          font-size: 1.25rem;
          line-height: 1;
        }

        .status-filter-card.status-all .status-filter-icon {
          background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%);
        }

        .status-filter-card.status-absent .status-filter-icon {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }

        .status-filter-card.status-late .status-filter-icon {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }

        .status-filter-card.status-leave .status-filter-icon {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
        }

        @media (max-width: 1024px) {
          .filter-group {
            min-width: 180px;
            max-width: 240px;
          }
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            text-align: center;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .filter-group {
            flex: 1 1 calc(50% - 0.6rem);
            min-width: 0;
            max-width: none;
          }

          .reports-grid {
            grid-template-columns: 1fr;
          }

          .report-navigation {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        @media (max-width: 480px) {
          .filter-group {
            flex: 1 1 100%;
          }
        }
      `}</style>
        </div>
    );
};

export default Reports;
